import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, FileOutput, Mail, RotateCw, ShieldCheck } from 'lucide-react';
import useAutoSave from '../../hooks/useAutoSave';
import MentionTextarea from '../../editor/shared/MentionTextarea';
import CreateColumnModal from '../../editor/shared/CreateColumnModal';
import * as SignService from '../../service/sign';
import { Card, Toggle, SenderSection } from './SenderSection';
import { emailMentionColumns } from './emailTokens';

const EXPIRY_OPTIONS = [7, 14, 30];

/**
 * Settings — one board's signing policy.
 *
 * These four cards were step 3 of the envelope builder, asked again on every
 * single request. They are board policy now: a board signs the same way every
 * time, and the builder no longer offers them at all. What a request still
 * decides for itself is who signs it and how long the links live.
 *
 * ONE FORM, ONE SAVE. Every change autosaves the whole object, because
 * `PUT /sign/board-settings` is a whole-object replace — a partial payload
 * resets what it omits to that field's default, which for the file column means
 * clearing the board's destination. Sending everything on every change makes
 * that impossible to get wrong, and it is also what the user expects: what is
 * on the screen is what the board does.
 *
 * The SENDER lives inside "Email to signers" rather than in a card of its own.
 * It is the one thing here the recipient sees before they open anything — the
 * From line — so it belongs with the subject and the message that arrive under
 * it, exactly where step 3 had it.
 */
export default function SettingsTab({
  monday,
  getSessionToken,
  boardId,
  board,
  onBoardChanged,
}) {
  const [settings, setSettings] = useState(null);
  const [senders, setSenders] = useState(undefined);
  const [loadError, setLoadError] = useState(false);
  const [columnModal, setColumnModal] = useState(null);
  const formRef = useRef(null);

  const { autoSave, formSaving } = useAutoSave({
    saveFn: async (token, payload) => {
      if (!boardId || !payload) return;
      await SignService.saveBoardSettings(token, boardId, payload);
    },
    getSessionToken,
    formRef,
    delay: 700,
  });

  const load = useCallback(async () => {
    if (!boardId) return;
    try {
      const token = await getSessionToken();
      const [s, sn] = await Promise.all([
        SignService.getBoardSettings(token, boardId),
        SignService.getSenders(token, boardId),
      ]);
      if (s?.ok) setSettings(s.settings);
      else setLoadError(true);
      setSenders(sn?.ok ? sn.senders || [] : []);
    } catch (err) {
      console.error('Failed to load board settings', err);
      setLoadError(true);
    }
  }, [boardId, getSessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const loadSenders = useCallback(async () => {
    try {
      const token = await getSessionToken();
      const res = await SignService.getSenders(token, boardId);
      setSenders(res?.ok ? res.senders || [] : []);
    } catch (err) {
      console.error('Failed to load sign senders', err);
      setSenders([]);
    }
  }, [boardId, getSessionToken]);

  /** Apply a patch and save the WHOLE object — never just the patch. */
  const patch = (next) => {
    setSettings((prev) => {
      const merged = { ...prev, ...next };
      formRef.current = merged;
      return merged;
    });
    autoSave();
  };
  const patchSending = (next) => patch({ sending: { ...settings.sending, ...next } });
  const patchOutput = (next) => patch({ output: { ...settings.output, ...next } });

  const handleColumnCreated = (column) => {
    const target = columnModal;
    setColumnModal(null);
    onBoardChanged?.();
    if (!target || !column?.id) return;
    if (target.forOutput === 'file') patchOutput({ fileColumnId: column.id });
    else if (target.forOutput === 'status') patchOutput({ statusColumnId: column.id });
  };

  /*
    Both full-page states use Track signatures' shape — `py-16 text-center`, no
    spinner. They were in a `max-w-2xl mx-auto` column with left-aligned text,
    so a one-line message sat against the left edge of an invisible column
    rather than in the middle of the tab.

    No spinner on the loading line either, for the same reason that tab has
    none: this resolves in one round trip, and a spinner that appears and
    vanishes inside 200ms is a flicker rather than reassurance.
  */
  if (loadError) {
    return (
      <div className='py-16 text-center text-sm text-gray-500'>
        Could not load this board’s settings. Reload the page to try again.
      </div>
    );
  }
  if (!settings) {
    return <div className='py-16 text-center text-sm text-gray-500'>Loading…</div>;
  }

  const fileColumns = board?.fileColumns || [];
  const statusColumns = board?.statusColumns || [];
  const sending = settings.sending || {};
  const output = settings.output || {};
  const senderVerified = (senders || []).some((s) => s.status === 'verified');

  return (
    <div className='flex-1 overflow-y-auto'>
      {/* px-4 py-5 and a text-base heading, the same as Track signatures and
          Templates — this was the only tab heading at text-sm, so it read as a
          section inside a page rather than the page. `space-y-4` stays: it
          spaces the cards below, which the other two tabs do not have. */}
      <div className='max-w-2xl w-full mx-auto px-4 py-5 space-y-4'>
        <div className='flex items-baseline justify-between'>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>Signing settings</h2>
            <p className='text-xs text-gray-500 mt-0.5'>
              Every request on this board follow this setup. Changes apply to requests sent from now on.
            </p>
          </div>
          <span className='text-xs text-gray-400 flex-shrink-0'>
            {formSaving ? (
              <span className='inline-flex items-center gap-1'>
                <RotateCw size={11} className='animate-spin' />
                Saving
              </span>
            ) : (
              'Saved'
            )}
          </span>
        </div>

        {/* ── Email to signers, sender included ── */}
        <Card icon={Mail} title='Email to signers'>
          <div className='space-y-3'>
            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>Subject</label>
              <MentionTextarea
                singleLine
                rows={1}
                value={sending.subject || ''}
                onChange={(v) => patchSending({ subject: v.slice(0, 200) })}
                boardColumns={emailMentionColumns(board?.prefillColumns)}
                placeholder='e.g. {sender.name} sent you {document.name} for signature'
                className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none'
              />
            </div>

            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>Message</label>
              <MentionTextarea
                rows={5}
                value={sending.message || ''}
                onChange={(v) => patchSending({ message: v.slice(0, 5000) })}
                boardColumns={emailMentionColumns(board?.prefillColumns)}
                className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none resize-y'
              />
              <p className='text-[11px] text-gray-400 mt-1'>
                Type <span className='font-mono bg-gray-100 px-1 rounded'>/</span> to insert who it is
                from, what they are signing, or a value from the item.
              </p>
            </div>

            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>Links expire after</label>
              {/* Not full width, unlike the selects below it: this one holds
                  "30 days", and a control stretched to the card's width reads as
                  if it were waiting for a longer answer. */}
              <select
                value={settings.expiresDays ?? 14}
                onChange={(e) => patch({ expiresDays: Number(e.target.value) })}
                className='w-40 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
              >
                {EXPIRY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} days
                  </option>
                ))}
              </select>
              {/* The one setting here a request can still overrule, so it says
                  so — otherwise a deadline changed on this screen and ignored
                  by a request in the builder reads as a bug. */}
            </div>

            {/* The From line — what a recipient reads before opening anything,
                so it sits with the subject and message that arrive under it. */}
            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>Sender</label>
              <SenderSection
                getSessionToken={getSessionToken}
                boardId={boardId}
                senders={senders}
                selectedId={settings.senderId ?? null}
                onSelect={(id) => patch({ senderId: id })}
                onReload={loadSenders}
              />
            </div>
          </div>
        </Card>

        {/* ── Identity verification ── */}
        <Card icon={ShieldCheck} title='Identity verification'>
          <Toggle
            on={settings.requireOtp !== false}
            onChange={(v) => patch({ requireOtp: v })}
            title='Email a one-time code'
            help='Before signing, each signer enters a 6-digit code sent to their address.'
          />
        </Card>

        {/* ── Notifications ── */}
        <Card icon={BellRing} title='Notifications'>
          <Toggle
            on={settings.notifySenderOnDecline !== false && senderVerified}
            disabled={!senderVerified}
            onChange={(v) => patch({ notifySenderOnDecline: v })}
            title='Tell the sender when someone declines'
            help={
              senderVerified
                ? 'A decline ends the request for everyone — this is the only thing that announces it.'
                : 'Verify a sender address above first — there is nowhere to send this yet.'
            }
          />
          <Toggle
            on={settings.notifySignersOnDecline === true}
            onChange={(v) => patch({ notifySignersOnDecline: v })}
            title='Tell the other signers too'
            help='Off by default: it discloses one signer’s decision to another.'
          />
        </Card>

        {/* ── Back to the board ── */}
        <Card icon={FileOutput} title='Back to the board'>
          <div className='space-y-3'>
            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>
                Signed documents go to
              </label>
              <select
                value={output.fileColumnId || ''}
                onChange={(e) => {
                  if (e.target.value === '__create__') setColumnModal({ forOutput: 'file' });
                  else patchOutput({ fileColumnId: e.target.value || null });
                }}
                className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
              >
                <option value=''>Choose a file column…</option>
                {fileColumns.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.title}
                  </option>
                ))}
                <option value='__create__'>+ Create new file column…</option>
              </select>
              {/* The only setting on this screen without a usable default, and
                  the send gate blocks on it — so an unset one is stated plainly
                  rather than left as an empty dropdown. */}
              {!output.fileColumnId && (
                <p className='text-[11px] mt-1' style={{ color: '#d83a52' }}>
                  Requests on this board cannot be sent until you choose one.
                </p>
              )}
            </div>

            <div>
              <label className='block text-[11px] text-gray-500 mb-0.5'>
                Track status in <span className='text-gray-400'>(optional)</span>
              </label>
              <select
                value={output.statusColumnId || ''}
                onChange={(e) => {
                  if (e.target.value === '__create__') setColumnModal({ forOutput: 'status' });
                  else patchOutput({ statusColumnId: e.target.value || null });
                }}
                className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
              >
                <option value=''>Don’t track status</option>
                {statusColumns.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.title}
                  </option>
                ))}
                <option value='__create__'>+ Create new status column…</option>
              </select>
              <p className='text-[11px] text-gray-400 mt-0.5'>
                Set to Sent, Viewed, Signed, Completed or Declined as the request moves.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {columnModal && (
        <CreateColumnModal
          isOpen
          monday={monday}
          boardId={boardId}
          onClose={() => setColumnModal(null)}
          onColumnCreated={handleColumnCreated}
          allowedTypes={columnModal.forOutput === 'file' ? ['file'] : ['status']}
          fieldLabel={
            columnModal.forOutput === 'file' ? 'Signed documents' : 'Signing status'
          }
        />
      )}
    </div>
  );
}
